/**
 * Created by Jerker Nord on 2021-08-25.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TrueGritStatics} from "../TrueGritStatics";
import {TrueGrit} from "../TrueGrit";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {StateMatrix} from "../utils/StateMatrix";
import {TrueGritSymbol} from "../reels/TrueGritSymbol";
import {ArrayUtils} from "../utils/ArrayUtils";
import {TrueGritData} from "../server/TrueGritData";
import {gsap} from "gsap";
import {TrueGritGameAssets} from "../TrueGritGameAssets";

export class TrueGritWaysView extends PIXI.Container implements StageLayer {

    private _waysText:PIXI.BitmapText;
    private _waysImage:PIXI.Sprite;
    private _waysContainer:PIXI.Container;
    private currentWays:number = 240;

    constructor() {
        super();
        this.name = "TrueGritWaysView";
        this.init();
    }

    private init() {
        this._waysContainer = new PIXI.Container();
        this._waysContainer.position = TrueGritStatics.WAYS_VIEW_POSITION;
        this.addChild(this._waysContainer);

        this._waysText = new PIXI.BitmapText(`${this.currentWays}`, {
            fontName : TrueGritGameAssets.BITMAP_FONT_WAYS_NUMBERS,
            fontSize : 42
        });
        this._waysText.anchor = 0.5;
        this._waysText.position.y = -17;
        this._waysContainer.addChild(this._waysText);

        this._waysImage = new PIXI.Sprite(TrueGrit.assetLoader.getTextures("ways")[0]);
        this._waysImage.anchor.set(0.5, 0);
        this._waysImage.position.set(4, -12);
        this._waysContainer.addChild(this._waysImage);

        this.onResize();
    }


    private setNumberOfWays(ways:number){
        this.currentWays = ways;
        this._waysText.text = `${ways}`;
    }

    public updateNumberOfWays(overrideValue?:number):gsap.core.Timeline{
        let ways:number;
        if (overrideValue){
            ways = overrideValue;
        } else {
            ways = this.calculateNumberOfWays();
        }

        const tl = gsap.timeline();

        if (ways == this.currentWays){
            tl.add(gsap.to(this,{duration:0.001}));
            return tl;
        }

        tl.add(gsap.to(this._waysText.scale, {duration:0.1, x:0.5,y:0.5}));
        tl.add(()=>this.setNumberOfWays(ways));
        tl.add(gsap.to(this._waysText.scale, {duration:0.1, x:1.0,y:1.0, ease: "back.out(0.7)"}));
        return tl;
    }

    onResize():void {
        this.position.y = 0;
        if(!NolimitApplication.isLandscape) {
            this.position.y = this.position.y - (TrueGritStatics.PORTRAIT_Y_OFFSET + NolimitApplication.screenBounds.top) / 2;
        }
    }

    private calculateNumberOfWays():number {
        const openPositionsMatrix = new StateMatrix<number>();
        openPositionsMatrix.initWithValue(6, 5, 1);
        TrueGritWaysView.addLocksToMatrix(openPositionsMatrix, TrueGrit.model.data);

        const positions:[number,number][] = StateMatrix.convertStateCoordinatesToTuples(openPositionsMatrix.getAllCoordinateForValue(1));

        const waysMatrix = new StateMatrix<number>();
        waysMatrix.initWithValue(6, 5, 0);


        for (let pos of positions){
            if (pos[1] == 0){
                //Top reel
                let symbol = <TrueGritSymbol>TrueGrit.allReelsView.topReelModel.getSymbolBySymbolPosition(pos[0])
                waysMatrix.matrix[pos[0]][pos[1]] += symbol.splitLevel == 0 ? 1 : symbol.splitLevel;


            }
            else if (pos[1] == 4 && (pos[0] >= 2 && pos[0] <= 4)){
                //Bot reel
                let symbol = <TrueGritSymbol>TrueGrit.allReelsView.bottomReelModel.getSymbolBySymbolPosition(pos[0] - 1);
                waysMatrix.matrix[pos[0]][pos[1]] += symbol.splitLevel == 0 ? 1 : symbol.splitLevel;

            } else if (pos[1] == 4 && pos[0] == 5){
                //Special wild symbol
                waysMatrix.matrix[5][4] = openPositionsMatrix.matrix[5][4];

            } else  {
                //Main reel
                let symbol = <TrueGritSymbol>TrueGrit.allReelsView.mainReelModels[pos[0]].getSymbol(pos[1]);
                waysMatrix.matrix[pos[0]][pos[1]] += symbol.splitLevel == 0 ? 1 : symbol.splitLevel;
            }
        }



        return TrueGritWaysView.getWaysFromMatrix(waysMatrix);

    }

    private static getWaysFromMatrix(waysMatrix:StateMatrix<number>):number {
        const reelSizes = [
            Math.max(ArrayUtils.addAll(waysMatrix.matrix[0]), 1),
            Math.max(ArrayUtils.addAll(waysMatrix.matrix[1]), 1),
            Math.max(ArrayUtils.addAll(waysMatrix.matrix[2]), 1),
            Math.max(ArrayUtils.addAll(waysMatrix.matrix[3]), 1),
            Math.max(ArrayUtils.addAll(waysMatrix.matrix[4]), 1),
            Math.max(ArrayUtils.addAll(waysMatrix.matrix[5]), 1)
        ];

        return ArrayUtils.multiplyAll(reelSizes);
    }


    private static addLocksToMatrix(waysMatrix:StateMatrix<number>, data:TrueGritData) {
        //removing non existing positions
        waysMatrix.matrix[0][0] = 0;
        waysMatrix.matrix[4][0] = 0;
        waysMatrix.matrix[5][0] = 0;

        //reel locks
        if(!data.botTrain.isRevealed[0]) {
            waysMatrix.matrix[2][4] = 0;
            waysMatrix.matrix[3][3] = 0;
            waysMatrix.matrix[4][2] = 0;
            waysMatrix.matrix[5][1] = 0;
        }
        if(!data.botTrain.isRevealed[1]) {
            waysMatrix.matrix[3][4] = 0;
            waysMatrix.matrix[4][3] = 0;
            waysMatrix.matrix[5][2] = 0;
        }
        if(!data.botTrain.isRevealed[2]) {
            waysMatrix.matrix[4][4] = 0;
            waysMatrix.matrix[5][3] = 0;
        }
        if(!data.botTrain.isRevealed[3]) {
            waysMatrix.matrix[5][4] = 0;
        }

    }
}